import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { EventDetails, Media } from '../../shared/models/eventDetails';
import { Subject, catchError, takeUntil, tap } from 'rxjs';
import { VIDEO, FILE_DOWNLOAD_ERROR } from '../../shared/constants/camera-profile-constant';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { TimeZoneService } from '../../shared/services/time-zone.service';
import { DashboardService } from '../../shared/services/dashboard.service';
import { CameraProfileService } from '../../shared/services/camera-profile.service';
import { GatewayStatus } from '../../shared/constants/enum';

@Component({
  selector: 'app-alerts-grid',
  templateUrl: './alerts-grid.component.html',
  styleUrls: ['./alerts-grid.component.scss'],
})
export class AlertsGridComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() gridData: EventDetails[];
  @Input() gatewayId: string;
  @Input() controllerId: string;
  @Input() totalNoRecords: number;
  @Input() paginatedData: PageEvent;
  @Input() eventId: number;
  @Input() isLoading: boolean;
  @Output() cameraPopOverClosed: EventEmitter<void> = new EventEmitter<void>();
  @Output() pageData: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() selectedData: EventEmitter<EventDetails[]> = new EventEmitter<EventDetails[]>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  public popOverCameraDetails: EventDetails | null;
  public currentZone = '';
  public hasNext = false;
  public hasPrevious = false;
  public selectedPopOverIndex = -1;
  public isOpen = false;
  public sessionToken: string;
  public gatewayStatus: GatewayStatus;
  public gatewaySessionStatus: string;
  private destroyed = new Subject();

  constructor(
    private timeZoneService: TimeZoneService,
    private messageService: MessageService,
    private dashboardService: DashboardService,
    private cameraProfileservice: CameraProfileService
  ) {
    this.currentZone = this.timeZoneService.getTimeZone();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.sessionToken = sessionStorage.getItem('access_token') ?? '';
    this.gatewaySessionStatus = sessionStorage.getItem('gatewayStatus') ?? '';
    this.setGatewayStatus();
    if (changes && changes['gridData'] && changes['gridData'].currentValue !== '') {
      this.gridData?.map((data: EventDetails) => {
        if (data) {
          data.gatewayId = this.gatewayId;
          data.gatewayStatus = this.gatewaySessionStatus as unknown as GatewayStatus;
          if (data?.media?.length > 0) {
            data.isVideo =
              data.media.filter(med => med.type.toLowerCase() === VIDEO && med.fileUrl !== '' && med.fileUrl !== null)?.length > 0;
          }
        }
      });
      this.popOverCameraDetails = this.selectedPopOverIndex !== -1 ? this.gridData[this.selectedPopOverIndex] : null;
      if (this.isOpen) {
        this.onEventClick(this.selectedPopOverIndex);
      }
    }
    if (this.eventId !== null && this.eventId > 0) {
      this.gridData?.forEach((element, i) => {
        if (element.eventId === this.eventId) {
          this.onImageClick(element, i);
        }
      });
    }

    if (this.paginator && this.paginatedData) {
      this.paginator.pageIndex = this.paginatedData.pageIndex;
      this.paginator.pageSize = this.paginatedData.pageSize;
    }
  }

  ngAfterViewInit(): void {
    if (this.paginator && this.paginatedData) {
      this.paginator.pageIndex = this.paginatedData?.pageIndex;
      this.paginator.pageSize = this.paginatedData?.pageSize;
    }
  }

  public downloadImage(cameraDetails: EventDetails): void {
    const urls = cameraDetails?.media.filter((url: Media) => url.fileUrl);
    if (urls.length > 0 && this.gatewayId && this.controllerId) {
      this.cameraProfileservice
        .downloadMediaAsZip(this.gatewayId, this.controllerId, [cameraDetails.eventId])
        .pipe(
          tap((data: string) => {
            if (data && data !== '') {
              this.dashboardService.downloadZipFile(data, this.controllerId);
            } else {
              this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
            }
          }),
          catchError(err => {
            this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
            throw err;
          }),
          takeUntil(this.destroyed)
        )
        .subscribe();
    } else {
      this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
    }
  }

  public checkBoxSelection(): void {
    const filteredFiles: EventDetails[] = this.gridData.filter((data: EventDetails) => data && data.isChecked);
    this.selectedData.emit(filteredFiles);
  }

  public onImageClick(eventDetails: EventDetails, index: number): void {
    this.isOpen = !this.isOpen;
    this.selectedPopOverIndex = index;
    const mapData: EventDetails = eventDetails;
    mapData.gatewayId = this.gatewayId;
    const pageEvent = this.paginatedData
      ? this.paginatedData
      : ({
          pageIndex: 0,
          previousPageIndex: 0,
          pageSize: 10,
          length: this.totalNoRecords,
        } as PageEvent);
    this.checkNext();
    this.checkPrevious(pageEvent);
    this.popOverCameraDetails = mapData;
  }

  public onEventClick(event: number): void {
    const action = event === this.selectedPopOverIndex - 1 ? 'prev' : 'next';
    this.selectedPopOverIndex = event;
    const pageEvent = this.paginatedData
      ? this.paginatedData
      : ({
          pageIndex: 0,
          previousPageIndex: 0,
          pageSize: 10,
          length: this.totalNoRecords,
        } as PageEvent);
    this.checkNext();
    this.checkPrevious(pageEvent);
    if (this.gridData[this.selectedPopOverIndex]) {
      const data: EventDetails = this.gridData[this.selectedPopOverIndex];
      this.popOverCameraDetails = data;
    } else if (this.hasNext) {
      pageEvent.pageIndex = action === 'next' ? pageEvent.pageIndex + 1 : pageEvent.pageIndex - 1;
      this.selectedPopOverIndex = action === 'next' ? 0 : pageEvent.pageSize - 1;
      this.pageData.emit(pageEvent);
    }
  }

  public checkNext(): void {
    this.hasNext = this.gridData.length > this.selectedPopOverIndex + 1;
  }

  public checkPrevious(pageEvent: PageEvent): void {
    this.hasPrevious = this.selectedPopOverIndex <= 0 && pageEvent.pageIndex === 0 ? false : true;
  }

  public pageEvent(event: PageEvent): void {
    this.selectedData.emit([]);
    this.pageData.emit(event);
  }

  public closeCameraPopOver(): void {
    this.popOverCameraDetails = null;
    this.isOpen = false;
    this.cameraPopOverClosed.emit();
  }

  public setGatewayStatus(): void {
    if (this.gatewaySessionStatus === '1') {
      this.gatewayStatus = GatewayStatus.Active;
    } else if (this.gatewaySessionStatus === '2') {
      this.gatewayStatus = GatewayStatus.Disconnected;
    } else if (this.gatewaySessionStatus === '3') {
      this.gatewayStatus = GatewayStatus.Inactive;
    }
  }

  public updateParentData(event: EventDetails): void {
    this.gridData[this.selectedPopOverIndex] = event;
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }
}
